import { Subject, Observable } from 'rxjs'

import { Injectable, Inject } from '@angular/core'

import { auth } from 'firebase'
import { AngularFireAuth } from 'angularfire2/auth'
import { AngularFireStorage } from 'angularfire2/storage'

import { ApiService } from '../data/api.service'
import { FirestoreService } from '../data/firestore.service'
import { FIREBASE_SETTINGS } from '../data/firebase.settings.token'
import { Secure } from '../data/secure'

import { User, CredentialItem, BasicUser, ActionItem } from './interfaces'
import {
    NotEnoughPermissionsError,
    UserDisabledError,
    UserHasNoCredentialsError,
    WrongUserInformationError,
    LoginInterruptedByUserError,
    TooManyLoginAttemptsError
} from './errors'

import { InjectorService } from '../services/injector.service'
import { LoginComponent } from './login/login.component'
import { RecaptchaComponent } from './recaptcha/recaptcha.component'
import { PinComponent } from './pin/pin.component'
import * as firebase from '../../secure/payments/utilities';
import { BehaviorSubject } from 'rxjs';
import { MainService } from './../../front/services/main.service'
import { Router } from '@angular/router';

@Injectable()
export class AuthService {
    static ACCESS_PERMISSION = 'ACCESS';
    static user: User;
    static users: { [uid: string]: User | BasicUser } = {};
    private userRole: any = new BehaviorSubject('');
    userRoleObj = this.userRole.asObservable();
    loggedOut: Subject<void> = new Subject<void>();
    confirmation: auth.ConfirmationResult;

    constructor(
        private injector: InjectorService,
        private firebaseAuth: AngularFireAuth,
        private firestore: FirestoreService,
        private storage: AngularFireStorage,
        private api: ApiService,
        private userData: MainService,
        private router: Router,
        @Inject(FIREBASE_SETTINGS) private settings: any
    ) { }

    get loginScreen() {
        const component = this.injector.getComponent<LoginComponent>('LOGIN', LoginComponent);
        component.auth = this;
        return component
    }

    get pinScreen() {
        const component = this.injector.getComponent<PinComponent>('PIN', PinComponent);
        component.auth = this;
        return component
    }

    get recaptchaVerifier() {
        return this.injector.getComponent<RecaptchaComponent>('CAPTCHA', RecaptchaComponent)
    }

    logout() {
        return this.firebaseAuth.auth.signOut().then(() => {
            AuthService.user = null;
            let role = '';
            this.userData.userObj.subscribe((data: string) => {
                role = data;
            });
            if (role === 'admin') {
                location.assign('/secured/#admin')
            } else {
                location.assign('/secured')
            }
            this.loggedOut.next(void 0)
        })
    }

    authenticate(): Observable<User> {
        return this.firebaseAuth.user
            .mergeMap(user =>
                user ?
                    Observable.of(user)
                    : this.loginScreen.authenticate()
                        .catch(error => this.handleError(error))
            )
            .mergeMap((user: User) =>
                user ?
                    user === AuthService.user ?
                        Observable.of(user)
                        :
                        this.getSecurityConfig()
                            .mergeMap(() =>
                                this.getUserInfo(AuthService.user = user)
                                    .map(current => {
                                        user.defaultBusiness = current.business;
                                        user.defaultUser = current.defaultUser;
                                        user.currentUser = user;
                                        return user
                                    })
                                    .catch(error => this.handleError(error))
                            )
                    :
                    Observable.of(null)
            )
        // TODO Show "Connection error" screen when connection is interrupted
        // TODO Show prompt to input email when not stored
        // TODO Show error screen when function fails
        // TODO Show PIN screen when credentials require it
        // TODO Throw LoginInterruptedByUserError
        // TODO Throw TooManyLoginAttemptsError
        // TODO firebaseAuth.auth.onAuthStateChanged
    }

    handleError(error: Error): Observable<Error | null> {
        const defaultHandler = err => Observable.of(err);

        const handlers: { [name: string]: (err: Error) => Observable<Error | null> } = {
            LoginInterruptedByUserError: err => Observable.of(err),
            TooManyLoginAttemptsError: err => Observable.of(err),
            NotEnoughPermissionsError: err => Observable.of(err),
            UserDisabledError: err => Observable.of(err),
            UserHasNoCredentialsError: err => Observable.of(err),
            WrongUserInformationError: err => Observable.of(err),
            ConnectionError: err => Observable.of(err),
            ExtendedError: defaultHandler,
            Error: defaultHandler,
            undefined: defaultHandler
        };

        return (handlers[error.name || error['code']] || defaultHandler)(error)
    }

    getSecurityConfig(): Observable<Secure> {
        return this.firestore.doc('domain/clickInfo')
            .map(value =>
                ApiService.initializeSecure(value.innovation, value.vector, value.password, value.click) || null
            )
    }

    getUserInfo(user: User | BasicUser): Observable<User | BasicUser> {
        const uId = user.uid;
        this.userData.userObj.subscribe(data => {
            console.log('DATA USER INFO AUTH ==============>', data)
        });
        return AuthService.users[uId] ?
            Observable.of(user = AuthService.users[uId]) :
            Observable.combineLatest(
                this.firestore.doc(`user/${uId}`, ['userName', 'secret']),
                this.firestore.doc(
                    `individual/${uId}`,
                    ['birthday', 'cityDocument', 'document', 'email', 'lastName', 'name', 'phoneNumber', 'urlProfilePhoto']
                ),
                this.firestore.doc(`customer/${uId}`, [['data', 'flatParse']]),
                this.firestore.doc(`userOperator/${uId}`, [['data', 'flatParse']]),
                this.firestore.doc(`userBusiness/${uId}`, [['data', 'flatParse'], 'businessCode']),
                this.firestore.doc(`userRoleDevice/${uId}`),
            )
                .mergeMap(([info, individual, customer, operator, businessData, devices]) => {
                    Object.assign(user, info, { info, individual, customer, devices, operator, businessData });
                    console.log(info);
                    if (info && info.status === 'ACTIVE') {
                        return Observable.combineLatest(
                            this.getUserCredentials(user),
                            (businessData && businessData.businessId) ? this.getBusiness(businessData.businessId) : Observable.of(null),
                            (businessData && businessData.businessId) ? this.getGiftCard(businessData.businessId) : Observable.of(null)
                        )
                    } else {
                        console.log('error', this.logout());
                        return Observable.throw(new UserDisabledError)
                    }
                })
                .mergeMap(([credentials, business, giftCard]) => {
                    console.log(credentials);
                    if (Array.isArray(credentials) && credentials.length) {

                        console.log('mergeMap credentials', user);
                        console.log('mergeMap credentials', credentials);
                        user.permissions = Object.assign(
                            { [AuthService.ACCESS_PERMISSION]: true },
                            user.permissions,
                            credentials.reduce(
                                (list, credential) => Object.assign(list, (credential ? credential.permissions : {})), {}
                            )
                        );
                        // Todo Add giftCard values
                        if (!giftCard) {
                            // user.currentCredential.landing.push(giftCardOption);
                        }
                        console.log('giftCard', business);
                        console.log('giftCard', giftCard);
                        user.credentials = credentials;
                        for (let i = 0; i < credentials.length; i++) {
                            const temp = credentials[i];
                            if (temp['switches']) {
                                if (user['userType'] === 'ADMIN' && temp['status'] === 'ACTIVE') {
                                    temp['switches'] = Object.assign({}, temp['switches'], { GIFTCARD: true });
                                } else {
                                    temp['switches'] = Object.assign({}, temp['switches'], { GIFTCARD: false });
                                }

                                if (user['userType']['BUSINESS'] === true && temp['status'] === 'ACTIVE' && user['statusBusiness'] === 'ACTIVE') {
                                    {
                                        if (!giftCard || Object.keys(giftCard).length === 0) {
                                            temp['switches'] = Object.assign({}, temp['switches'], { REMOTEGIFTCARD: false });
                                            console.log('NO TIENE GIFTCARD');
                                            // user.currentCredential.landing.push(giftCardOption);
                                        }
                                    }
                                }
                                console.log(temp['switches']);
                                Object.keys(temp['switches']).forEach((key) => {
                                    console.log(key);
                                    console.log(temp.permissions);
                                    if (key in temp.permissions) {
                                        temp.permissions[key] = temp['switches'][key] ? true : false;
                                        console.log(temp);
                                    }
                                });
                            }
                            console.log(temp.permissions);
                            console.log('=======================');
                            // ADMIN
                            if (user['userType'] === 'ADMIN' && temp['status'] === 'ACTIVE') {
                                console.log('======   Admin');
                                console.log(temp);
                                this.switchCredential(temp);
                                break;
                            }

                            // CLIENTE
                            if (user['userType']['CUSTOMER'] === true && temp['status'] === 'ACTIVE' && user['statusCustomer'] === 'ACTIVE') {
                                console.log('======   Cliente');
                                console.log(temp);
                                this.switchCredential(temp);
                                this.router.navigateByUrl('/secured/landing');
                                break;
                            }

                            // ADMIN SECUNDARIO
                            if (user['userType']['OPERATOR'] === true && temp['status'] === 'ACTIVE' && user['statusOperator'] === 'ACTIVE') {
                                console.log('======   Admin sec');
                                console.log(temp);
                                this.switchCredential(temp);
                                this.router.navigateByUrl('/secured/landing');
                                break;
                            }

                            // POS Y ADMIN BUSINES
                            if (user['userType']['BUSINESS'] === true && temp['status'] === 'ACTIVE' && user['statusBusiness'] === 'ACTIVE') {
                                console.log('======   Cliente');
                                console.log(temp);
                                this.switchCredential(temp);
                                this.router.navigateByUrl('/secured/landing');
                                break;
                            }
                            if (user['userType']['BUSINESS'] === true && temp['status'] === 'INACTIVE' && user['statusBusiness'] === 'INACTIVE') {
                                console.log('error usuario sin autorizacion');
                                this.logout();
                                break;
                            }
                        }
                        user.business = business;
                        AuthService.users[uId] = user;
                        console.log(user);
                        if (user.settings && user.settings.RequirePin) {
                            return Observable.of(this.pinScreen.checkPin)
                        } else {
                            return Observable.of(user)
                        }
                    } else {
                        return Observable.throw(new UserHasNoCredentialsError)
                    }
                }).mergeMap((returnedUser) => {
                    if (user.permissions.NO_WEB_ACCESS) {
                        return Observable.throw(new NotEnoughPermissionsError({ message: 'User is not allowed to access web application' }))
                    } else if (user.currentCredential && user.currentCredential.role === 'business' && !user.business) {
                        return Observable.throw(new WrongUserInformationError({ message: 'Business is not defined' }))
                    } else {
                        return Observable.of(returnedUser)
                    }
                })
    }

    getBusiness(businessId: string) {
        return this.firestore.doc(
            `business/${businessId}`,
            ['businessCity', 'businessCode', 'businessName', 'businessType', 'commerceCode', 'merchantType', 'urlBusinessPhoto']
        )
    }
    getGiftCard(businessId: string = '') {
        return this.firestore.col(`giftCard`, [],
            ref => ref.where('businessId', '==', businessId));
    }

    getUserCredentials(user: User | BasicUser): Observable<CredentialItem[]> {
        return Observable.combineLatest(
            this.firestore.doc(`domain/UserCredentials`),
            this.firestore.doc(`domain/UserActions`),
            this.firestore.doc(`domain/permissionsDefault`),
            this.firestore.doc(`domain/switches`),
            this.firestore.doc(`domain/paymentAnnulledConfig`)
        ).map(([credentials, actions, permissions, switches, paymentAnnulledConfig]) =>
            user.credentials instanceof Array ?
                user.credentials.map(cred => {
                    const credential = credentials[cred.role] && credentials[cred.role][cred.level];
                    console.log(cred);
                    console.log(permissions);
                    const image = credential.identity.image.url.replace(
                        /(?:\$([.\w]+))/g,
                        (_, path) =>
                            cred[path] ||
                            path.split('.').reduce(
                                (result, part) =>
                                    (result || { user, cred })[part] || '',
                                null
                            ) || `assets/images/ghost_circle.svg`
                    );
                    console.log('credentials', credentials);
                    console.log('actions', actions);
                    console.log('permissions', permissions);
                    console.log('user.permissions', user.permissions);
                    console.log('switches', switches);
                    console.log('user', user);
                    console.log('user', AuthService.user);
                    // default/${cred.role}.png
                    return credential ?
                        {
                            uId: user.uid,
                            role: cred.role || 'default',
                            level: cred.level || 'default',
                            provider: cred.provider || 'default',
                            landing: credential.landing ?
                                credential.landing.map(item =>
                                    item.split('/').reduce((path, action) =>
                                        path ? path[action] || null : action === 'actions' ? actions : null,
                                        null
                                    )
                                ) :
                                [],
                            menu: credential.menu ?
                                credential.menu.map(item =>
                                    item.split('/').reduce((path, action) =>
                                        path ? path[action] || null : action === 'actions' ? actions : null,
                                        null
                                    )
                                ) :
                                [],
                            permissions: (cred.role === 'customer' && cred.level === 'main') ?
                                permissions['CUSTOMER'] :
                                (cred.role === 'business' && cred.level === 'operator') ?
                                    permissions['BUSINESS']['operator'] ? permissions['BUSINESS']['operator'] :
                                        user.permissions : (cred.role === 'business' && cred.level === 'manager') ?
                                    AuthService.user && AuthService.user['userType'] && AuthService.user['userType']['OPERATOR']?
                                        AuthService.user.permissions  :
                                        Object.assign({},permissions['BUSINESS']['manager'],user.permissions)
                                            :
                                        (cred.role === 'admin' && cred.level === 'admin') ?
                                            user.permissions : {} ||
                                            {},
                            switches: switches,
                            paymentAnnulledConfig: paymentAnnulledConfig,
                            settings: credential.settings || {},
                            identity: credential.identity || {},
                            status: (cred.role === 'customer' && cred.level === 'main') ?
                                user['statusCustomer'] :
                                (cred.role === 'business' && cred.level === 'operator') ?
                                    user['statusBusiness'] : (cred.role === 'business' && cred.level === 'manager') ?
                                        user['statusBusiness'] :
                                        (cred.role === 'admin' && cred.level === 'admin') ?
                                            user['userType'] === 'ADMIN' ? 'ACTIVE' : user['operator']['status'] === 'ACTIVE' ?
                                                user['statusOperator'] : '' : '' ||
                                            '',
                            imageAsync: image ?
                                (/^https?:/.test(image) ? Observable.of(image) : this.storage.ref(image).getDownloadURL())
                                    .catch(err => Observable.of(`assets/images/ghost_circle.svg`))
                                    .toPromise<string>() :
                                new Promise<string>(resolve => resolve(`assets/images/ghost_circle.svg`))
                        } :
                        {
                            landing: [],
                            permissions: {},
                        }
                }) :
                []
        );
        /***********************************************************************************************
         * CODIGO ANTERIOR
         * ====== ========
         *  (/^https?:/.test(image) ? Observable.of(image) : this.storage.ref(image).getDownloadURL())
         *  .catch(err => Observable.of(`default/${cred.role}.png`))
         *  .toPromise<string>() :
         *  new Promise<string>(resolve => resolve(`default/${cred.role}.png`))
         **********************************************************************************************/
    }

    switchUser(uid) {
        return this.getUserInfo({ uid })
            .map(user => {
                AuthService.user.currentUser = user as User;
                AuthService.user.business = user.business;
                return this.switchCredential(user.credentials[0])
            })
    }

    switchBusiness(businessId) {
        console.log(businessId);
        const user = AuthService.user;
        return Observable.combineLatest(
            this.getBusiness(businessId),
            this.getUserCredentials(
                { uid: user.uid, credentials: [{ role: 'business', level: 'manager', provider: businessId, landing: [], permissions: {} }] }
            )
        )
            .map(([business, credentials]) => {
                console.log('business', business);
                console.log('credentials', credentials);
                this.switchCredential(credentials.pop());
                business = Object.assign({}, business, { businessId });
                user.business = business;
                return user
            })
    }

    setPermitionsByTypeuser() {
        const user = AuthService.user;
        if (user.currentCredential.permissions) {
            user.currentUser.permissions = Object.assign({ [AuthService.ACCESS_PERMISSION]: true }, user.currentCredential.permissions);
            return user;
        } else {
            return user;
        }
    }
    switchCredential(credential: CredentialItem) {
        const user = AuthService.user;
        user.currentCredential = credential;
        if (!credential || credential.role !== 'business') {
            user.business = user.defaultBusiness
        }
        if (credential) {
            if (credential.uId === user.uid) {
                user.currentUser = user;
                //   credential.permissions?
                // user.currentUser.permissions = credential.permissions : {}
                user.business = user.defaultBusiness
            }
        }
        this.setPermitionsByTypeuser();
        // TODO Fire CredentialsChanged event
        return user
    }

    verifyPermissions(requiredPermissions: string | string[]): boolean {
        return AuthService.user && [].concat(requiredPermissions).reduce((clearance, permission) => {
            return clearance && AuthService.user.permissions[permission]
        }, true)
    }

    verifyUserVerificationEmail(email: string, type: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const payRealTime = 'verifyAccount';
            const data = { body: { account: email, userType: type } };
            this.api.postRequest(payRealTime, data).subscribe((x) => {
                console.log(x);
                const temp = this.verifyAndSendMail(email, type);
                resolve(temp);
            }, error1 => {
                console.log(error1.error);
                reject(new Error(error1.error['messages']['es']))
            });
        });
    }
    verifyAndSendMail(email, type): Promise<any> {
        return new Promise((resolve, reject) => {
            const payRealTime = 'verifyAndSendMail';
            const data = {
                body: {
                    'email': email,
                    'userType': type
                }
            };
            this.api.postRequest(payRealTime, data).subscribe(result => {
                console.log(result);
                resolve(result);
            }, error1 => {
                console.log(error1.error);
                reject(new Error(error1.error['messages']['es']))
            });
        });

    }
    sendVerificationEmail(email: string): Promise<any> {
        return this.firebaseAuth.auth.sendSignInLinkToEmail(
            email,
            { url: `https://${this.settings.authDomain}/signin`, handleCodeInApp: true }
        )
            .then((response) => {
                console.log('Completado');
                localStorage.setItem('signInEmail', email)
            })
            .catch(reason => {
                throw new Error({
                    'auth/expired-action-code': 'El código expiró. Por favor intente nuevamente.',
                    'auth/invalid-email': 'El correo electrónico no es correcto.',
                    'auth/user-disabled': 'Usuario deshabilitado.',
                }[reason.code] || reason)
            })
    }

    verifyEmailLink(url: string): Promise<auth.UserCredential> {
        // TODO: VERIFICAR EMAIL
        return new Promise<auth.UserCredential>((resolve, reject) => {
            // url = `https://${this.settings.authDomain}/signin`
            const email = localStorage.getItem('signInEmail');
            console.log('verifyEmailLink', url, email);
            if (email) {
                if (this.firebaseAuth.auth.isSignInWithEmailLink(url)) {
                    this.firebaseAuth.auth.signInWithEmailLink(email, url)
                        .then(response => {
                            localStorage.removeItem('signInEmail');
                            resolve(response)
                        })
                        .catch(reason => {
                            throw new Error({
                                'auth/invalid-email': 'El nombre de usuario no es correcto.',
                                'auth/user-disabled': 'Usuario deshabilitado.',
                                'auth/expired-action-code': 'El enlace de activación expiró. Por favor vuelva a ingresar a la aplicación.',
                            }[reason.code] || reason)
                        })
                } else {
                    reject({})
                }
            }
        })
    }

    verifyUserVerificationSMS(number: string, type: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const payRealTime = 'verifyAccount';
            const data = { body: { account: number, userType: type } };
            this.api.postRequest(payRealTime, data).subscribe((x) => {
                const temp = this.sendVerificationSMS(number);
                resolve(temp);
            }, error1 => {
                console.log(error1.error);
                reject(new Error(error1.error['messages']['es']))
            });
        });
    }
    sendVerificationSMS(phoneNumber): Promise<auth.ConfirmationResult> {
        return this.firebaseAuth.auth.signInWithPhoneNumber(phoneNumber, this.recaptchaVerifier.verifier)
            .then(confirmation => {
                this.recaptchaVerifier.verifier.clear();
                this.recaptchaVerifier.collapse = '';
                return this.confirmation = confirmation
            })
            .catch(reason => {
                throw new Error({
                    'auth/captcha-check-failed': 'La validación reCAPTCHA es inválida, está expirada, o el dominio no está permitido.',
                    'auth/invalid-phone-number': 'El formato del número de teléfono no es válido.',
                    'auth/missing-phone-number': 'Se requiere un número de teléfono.',
                    'auth/quota-exceeded': 'La cuota de mensajes SMS ha sido excedida.',
                    'auth/user-disabled': 'Usuario deshabilitado.',
                    'auth/operation-not-allowed': 'Método de acceso no permitido.',
                }[reason.code] || reason)
            })
    }

    verifyConfirmationCode(code: string) {
        return this.confirmation.confirm(code)
            .catch(reason => {
                throw new Error({
                    'auth/invalid-verification-code': 'El código de verificación no es válido.',
                    'auth/missing-verification-code': 'Se requiere un código de verificación.',
                }[reason.code] || reason)
            })
    }

    verifyUserAuthValid(number: string, code: string, type: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const payRealTime = 'verifyAccount';
            const data = { body: { account: number, userType: type } };
            this.api.postRequest(payRealTime, data).subscribe((x) => {
                const temp = this.verifyUserAuth(number, code);
                console.log(x);
                resolve(temp);
            }, error1 => {
                console.log(error1.error);
                reject(new Error(error1.error['messages']['es']))
            });
        });
    }
    verifyUserAuth(username: string, password: string): Promise<auth.UserCredential> {
        console.log(username);
        const mail = /^[\u0020-\uffff]+@(?:[\u0020-\uffff]+\.)+?[\u0020-\uffff]{2,}$/.test(username) ?
            username : `${username}@${this.settings.userAuthDomain}`;

        return this.firebaseAuth.auth.signInWithEmailAndPassword(mail, password)
            .catch(reason => {
                throw new Error({
                    'auth/invalid-email': 'El nombre de usuario no es correcto.',
                    'auth/user-disabled': 'Usuario deshabilitado.',
                    'auth/user-not-found': 'El usuario no existe.',
                    'auth/wrong-password': 'Contraseña incorrecta.',
                }[reason.code] || reason)
            })
    }

    verifyPin(uId: string, pin: string) {
        return this.api.postEncrypted('validatePin', {
            body: { text: JSON.stringify({ uId, pin, appType: 'CUSTOMER' }) }
        })
    }

}
